import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Radio,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

interface OutputConfig {
  id: number;
  name: string;
  contentType: string; // 关联内容类型
  outputFormat: 'video' | 'image' | 'text' | 'audio';
  description: string;
  targetPlatforms: string[]; // 目标发布平台
  generationFrequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  quantity: number; // 每日/周/月生成数量
  schedule: string; // 生成时间计划
  status: 'active' | 'inactive';
  priority: number;
  createTime: string;
  updateTime: string;
}

// Demo数据
const initialData: OutputConfig[] = [
  {
    id: 1,
    name: '抖奶擦边视频',
    contentType: 'bounce_edge_video',
    outputFormat: 'video',
    description: '每日生成10个抖奶擦边视频，用于吸引用户关注',
    targetPlatforms: ['TikTok', 'Facebook', 'Instagram', 'YouTube'],
    generationFrequency: 'daily',
    quantity: 10,
    schedule: '09:00,14:00,20:00',
    status: 'active',
    priority: 1,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 2,
    name: '今日足球对战分析报告（视频）',
    contentType: 'football_analysis_video',
    outputFormat: 'video',
    description: '每日生成足球对战分析视频，包含今日重要比赛分析',
    targetPlatforms: ['TikTok', 'Facebook', 'YouTube', 'Instagram'],
    generationFrequency: 'daily',
    quantity: 3,
    schedule: '08:00,12:00,18:00',
    status: 'active',
    priority: 2,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 3,
    name: '美女直播预告图',
    contentType: 'beauty_live_preview',
    outputFormat: 'image',
    description: '每日生成美女主播直播预告图片',
    targetPlatforms: ['TikTok', 'Facebook', 'Instagram', 'Zalo'],
    generationFrequency: 'daily',
    quantity: 20,
    schedule: '10:00,15:00,19:00,22:00',
    status: 'active',
    priority: 3,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 4,
    name: '游戏攻略文章',
    contentType: 'game_strategy_article',
    outputFormat: 'text',
    description: '每周生成游戏攻略文章',
    targetPlatforms: ['Facebook', 'Zalo', 'Instagram'],
    generationFrequency: 'weekly',
    quantity: 5,
    schedule: '周一,周三,周五 10:00',
    status: 'active',
    priority: 4,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 5,
    name: '赛事解说音频',
    contentType: 'match_commentary_audio',
    outputFormat: 'audio',
    description: '重要赛事期间生成解说音频',
    targetPlatforms: ['Bigo Live', 'Zalo', 'YouTube'],
    generationFrequency: 'custom',
    quantity: 10,
    schedule: '赛事日 20:00',
    status: 'active',
    priority: 5,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 6,
    name: '抽奖活动视频',
    contentType: 'lottery_activity_video',
    outputFormat: 'video',
    description: '活动期间每日生成抽奖活动宣传视频',
    targetPlatforms: ['TikTok', 'Facebook', 'Instagram'],
    generationFrequency: 'daily',
    quantity: 5,
    schedule: '09:00,18:00',
    status: 'active',
    priority: 6,
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
];

const PLATFORM_OPTIONS = [
  'Facebook',
  'TikTok',
  'YouTube',
  'Instagram',
  'Shopee',
  'Zalo',
  'Lazada',
  'Bigo Live',
  'Tiki',
  'TikTok Shop'
];

const ContentTypeOptions = [
  { value: 'bounce_edge_video', label: '抖奶擦边视频' },
  { value: 'football_analysis_video', label: '足球对战分析视频' },
  { value: 'beauty_live_preview', label: '美女直播预告图' },
  { value: 'game_strategy_article', label: '游戏攻略文章' },
  { value: 'match_commentary_audio', label: '赛事解说音频' },
  { value: 'lottery_activity_video', label: '抽奖活动视频' },
];

const OutputConfig: React.FC = () => {
  const [data, setData] = useState<OutputConfig[]>(initialData);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<OutputConfig | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: OutputConfig) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      targetPlatforms: record.targetPlatforms,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    setData(prev => prev.filter(item => item.id !== id));
    message.success('删除成功');
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (editingRecord) {
        setData(prev =>
          prev.map(item =>
            item.id === editingRecord.id
              ? { ...item, ...values, updateTime: new Date().toLocaleString('zh-CN') }
              : item
          )
        );
        message.success('更新成功');
      } else {
        const newRecord: OutputConfig = {
          id: Date.now(),
          ...values,
          status: 'active',
          createTime: new Date().toLocaleString('zh-CN'),
          updateTime: new Date().toLocaleString('zh-CN'),
        };
        setData(prev => [...prev, newRecord]);
        message.success('新增成功');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const filteredData = data.filter(item =>
    item.name.includes(searchText) ||
    item.description.includes(searchText)
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '输出物名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '内容类型',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 150,
    },
    {
      title: '输出格式',
      dataIndex: 'outputFormat',
      key: 'outputFormat',
      width: 100,
      render: (format: string) => {
        const map: Record<string, { text: string; color: string }> = {
          video: { text: '视频', color: 'red' },
          image: { text: '图片', color: 'blue' },
          text: { text: '文本', color: 'green' },
          audio: { text: '音频', color: 'orange' },
        };
        const info = map[format] || { text: format, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '目标平台',
      dataIndex: 'targetPlatforms',
      key: 'targetPlatforms',
      width: 250,
      render: (platforms: string[]) => (
        <Space wrap>
          {platforms.map(platform => (
            <Tag key={platform}>{platform}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '生成频率',
      dataIndex: 'generationFrequency',
      key: 'generationFrequency',
      width: 120,
      render: (freq: string) => {
        const map: Record<string, string> = {
          daily: '每日',
          weekly: '每周',
          monthly: '每月',
          custom: '自定义',
        };
        return map[freq] || freq;
      },
    },
    {
      title: '生成数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: '生成计划',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 200,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: OutputConfig) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>输出物配置</h1>
        <p>配置运营需要的输出物，如抖奶擦边视频、今日足球对战分析报告等</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索输出物名称或描述"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('刷新成功')}>
            刷新
          </Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑输出物配置' : '新增输出物配置'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="输出物名称"
            rules={[{ required: true, message: '请输入输出物名称' }]}
          >
            <Input placeholder="如：抖奶擦边视频" />
          </Form.Item>
          <Form.Item
            name="contentType"
            label="内容类型"
            rules={[{ required: true, message: '请选择内容类型' }]}
          >
            <Select placeholder="请选择内容类型">
              {ContentTypeOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="outputFormat"
            label="输出格式"
            rules={[{ required: true, message: '请选择输出格式' }]}
          >
            <Radio.Group>
              <Radio value="video">视频</Radio>
              <Radio value="image">图片</Radio>
              <Radio value="text">文本</Radio>
              <Radio value="audio">音频</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea rows={3} placeholder="描述该输出物的用途和特点" />
          </Form.Item>
          <Form.Item
            name="targetPlatforms"
            label="目标平台"
            rules={[{ required: true, message: '请选择至少一个目标平台' }]}
          >
            <Checkbox.Group>
              {PLATFORM_OPTIONS.map(platform => (
                <Checkbox key={platform} value={platform}>{platform}</Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            name="generationFrequency"
            label="生成频率"
            rules={[{ required: true, message: '请选择生成频率' }]}
          >
            <Radio.Group>
              <Radio value="daily">每日</Radio>
              <Radio value="weekly">每周</Radio>
              <Radio value="monthly">每月</Radio>
              <Radio value="custom">自定义</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="schedule"
            label="生成计划"
            rules={[{ required: true, message: '请输入生成计划' }]}
          >
            <Input placeholder="如：09:00,14:00,20:00" />
          </Form.Item>
          <Form.Item name="priority" label="优先级" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="active">
            <Radio.Group>
              <Radio value="active">启用</Radio>
              <Radio value="inactive">禁用</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OutputConfig;

